import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Trash2, Download, Image as ImageIcon, Maximize2 } from 'lucide-react';

type LibraryJob = {
  id: string;
  fileName?: string;
  jobType?: string;
  status?: string;
  statusLabel?: string;
  versionLabels?: string[];
  versionCount?: number;
  displayVersionLabel?: string | null;
  createdAt?: string;
  thumbnailUrl?: string | null;
  hasRestoration?: boolean;
  hasUpscale?: boolean;
  hasPrintReady?: boolean;
  upscaleFactors?: number[];
  downloadUrl?: string | null;
  canUpscale?: boolean;
};

function formatDate(iso?: string) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

const LibraryThumb: React.FC<{ job: LibraryJob }> = ({ job }) => {
  const [failed, setFailed] = useState(false);
  const alt = `${job.fileName || 'Photo'}${job.displayVersionLabel ? ` — ${job.displayVersionLabel}` : ''}`;

  if (!job.thumbnailUrl || failed) {
    return (
      <div className="kin-library-thumb-placeholder" aria-hidden>
        <ImageIcon className="kin-library-thumb-icon" />
        <span>Photo</span>
      </div>
    );
  }

  return (
    <img
      src={job.thumbnailUrl}
      alt={alt}
      className="kin-library-thumb-img"
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
};

export const LibraryPage: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<LibraryJob[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.jobs(token);
      setJobs((res.jobs || []) as LibraryJob[]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const remove = async (id: string) => {
    setDeletingId(id);
    try {
      await api.deleteJob(id, token);
      setJobs((prev) => prev.filter((j) => j.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const openJob = (job: LibraryJob) => {
    navigate(`/app/library/${encodeURIComponent(job.id)}`);
  };

  return (
    <main className="kin-page kin-library-page">
      <div className="kin-page-head">
        <h1>Library</h1>
        <div className="kin-library-head-actions">
          <Link to="/app/upscale" className="kin-btn kin-btn-ghost">
            Prepare for Print
          </Link>
          <Link to="/app" className="kin-btn kin-btn-primary">
            New restoration
          </Link>
        </div>
      </div>
      <p className="kin-section-lede">
        Your photos with restorations and print-ready versions. Open a photo to review history and
        prepare for print.
      </p>
      {error && <p className="kin-banner kin-banner-error">{error}</p>}
      {loading ? (
        <p>Loading…</p>
      ) : jobs.length === 0 ? (
        <p>
          No photos yet. <Link to="/app">Restore</Link> or{' '}
          <Link to="/app/upscale">Prepare for Print</Link>.
        </p>
      ) : (
        <ul className="kin-library-grid">
          {jobs.map((job) => {
            const metaParts = [
              ...(job.versionLabels?.length
                ? job.versionLabels
                : [job.statusLabel || 'Photo']),
              job.createdAt ? formatDate(job.createdAt) : null,
            ].filter(Boolean);

            return (
              <li key={job.id} className="kin-library-card">
                <button
                  type="button"
                  className="kin-library-card-media kin-library-card-media-btn"
                  onClick={() => openJob(job)}
                  aria-label={`Open ${job.fileName || 'photo'}`}
                >
                  <span className="kin-library-select-slot" aria-hidden="true" />
                  <LibraryThumb job={job} />
                </button>
                <div className="kin-library-card-body">
                  <strong className="kin-library-card-title" title={job.fileName || 'Photo'}>
                    {job.fileName || 'Photo'}
                  </strong>
                  <span className="kin-library-card-meta">{metaParts.join(' · ')}</span>
                  {job.displayVersionLabel ? (
                    <span className="kin-library-card-meta">
                      Showing: {job.displayVersionLabel}
                    </span>
                  ) : null}
                  <div className="kin-library-card-actions">
                    <button
                      type="button"
                      className="kin-btn kin-btn-ghost"
                      onClick={() => openJob(job)}
                    >
                      Open
                    </button>
                    {job.canUpscale !== false ? (
                      <Link
                        className="kin-btn kin-btn-ghost"
                        to={`/app/library/${encodeURIComponent(job.id)}`}
                      >
                        <Maximize2 size={16} aria-hidden /> Prepare
                      </Link>
                    ) : null}
                    {job.downloadUrl ? (
                      <a
                        className="kin-btn kin-btn-ghost"
                        href={job.downloadUrl}
                        download
                      >
                        <Download size={16} aria-hidden /> Download
                      </a>
                    ) : null}
                    <button
                      type="button"
                      className="kin-btn kin-btn-ghost kin-library-delete"
                      disabled={deletingId === job.id}
                      onClick={() => remove(job.id)}
                    >
                      <Trash2 size={16} aria-hidden />
                      {deletingId === job.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
};
